import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogOut, Menu, X, ChevronDown, Sun, Moon, Loader2, Gift, Zap, CircleHelp, Clock, Settings, CreditCard, Shield } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import logoImage from "@/assets/logo.png";
import { useTheme, THEMES } from "@/contexts/ThemeContext";
import { usePolling } from "@/contexts/PollingContext";
import api from "../lib/api";
import { CREATOR_PROD_URL, getCreatorAppUrl } from "@/lib/creatorUrl";
import { useTutorial } from "@/tutorials/TutorialProvider";
import TrialReminderDialog from "@/components/TrialReminderDialog";
import { isUnpaidTrial, isTrialExpired, trialDismissKey, TRIAL_PRICING_PATH } from "@/lib/connectTrial";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";



const Navbar = ({ isAuthenticated, user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(true);
  const { theme, toggleTheme } = useTheme();
  const { isPolling, pollingJobs } = usePolling();
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [connectStatus, setConnectStatus] = useState(null);
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const { hasTutorial, openTutorial } = useTutorial();

  const openCreator = useCallback((path = "") => {
    const base = getCreatorAppUrl();
    const url = path ? `${base}${path.startsWith("/") ? path : `/${path}`}` : base;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const userInitial = (user?.name || user?.email || "?").charAt(0).toUpperCase();

  const isActive = (path) => location.pathname === path;

  // Per-store member section gating. `user.section_permissions` is null/undefined
  // for unrestricted users (owners, admins, direct subscribers) and an array of
  // allowed section keys for pure teammate members.
  const sectionPerms = user?.section_permissions;
  const canSection = (key) => !Array.isArray(sectionPerms) || sectionPerms.includes(key);

  const SPARKCONNECT_NAV = [
    { key: 'items', label: 'Items', path: '/items' },
    { key: 'listings', label: 'Listings', path: '/walmart/listings' },
    { key: 'inventory', label: 'Inventory', path: '/walmart/inventory' },
    { key: 'buybox', label: 'Buy Box', path: '/walmart/buybox' },
    { key: 'orders', label: 'Orders', path: '/walmart/orders' },
    { key: 'pricing', label: 'Pricing', path: '/walmart/pricing' },
    { key: 'incentives', label: 'Incentives', path: '/walmart/incentives' },
    { key: 'profit_loss', label: 'Profit & Loss', path: '/walmart/profit-loss' },
    { key: 'recon', label: 'Recon', path: '/walmart/recon' },
    { key: 'advertising', label: 'Advertising', path: '/walmart/advertising' },
    { key: 'campaigns', label: 'Campaigns', path: '/walmart/campaigns' },
    { key: 'insights', label: 'Insights', path: '/walmart/insights' },
    { key: 'marketing', label: 'Marketing', path: '/walmart/marketing' },
    { key: 'compare', label: 'Compare', path: '/walmart/compare' },
    { key: 'feeds', label: 'Feeds', path: '/walmart/feeds' },
    { key: 'wfs_services', label: 'WFS Services', path: '/walmart/wfs-services' },
    { key: 'wfs_estimator', label: 'WFS Estimator', path: '/wfs-estimator' },
    { key: 'data_sync', label: 'Data Sync', path: '/walmart/data-sync' },
  ];
  const visibleSparkConnectNav = SPARKCONNECT_NAV;

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while mobile menu is open so the page can't scroll
  // behind the open overlay. Also hide SparkChat so it cannot sit on the drawer.
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('overflow-hidden', 'mobile-nav-open');
    } else {
      document.body.classList.remove('overflow-hidden', 'mobile-nav-open');
    }
    return () => {
      document.body.classList.remove('overflow-hidden', 'mobile-nav-open');
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!isAuthenticated) { setIsAffiliate(false); return; }
    api.get(`/affiliates/me`)
      .then(r => setIsAffiliate(!!r.data))
      .catch(() => setIsAffiliate(false));
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setConnectStatus(null);
      setTrialModalOpen(false);
      return;
    }
    const loadStatus = () => {
      api.get("/connect/status")
        .then((r) => setConnectStatus(r.data))
        .catch(() => setConnectStatus(null));
    };
    loadStatus();
    const timer = setInterval(loadStatus, 60_000);
    const onChange = () => loadStatus();
    window.addEventListener("connect-status-changed", onChange);
    return () => {
      clearInterval(timer);
      window.removeEventListener("connect-status-changed", onChange);
    };
  }, [isAuthenticated]);

  const unpaidTrial = isUnpaidTrial(connectStatus);
  const trialExpired = isTrialExpired(connectStatus);
  const onSparkConnect = (location.pathname.replace(/\/$/, "") || "/") === "/spark-connect";

  useEffect(() => {
    if (!unpaidTrial || !user?.id || onSparkConnect) {
      setTrialModalOpen(false);
      return;
    }
    try {
      if (sessionStorage.getItem(trialDismissKey(user.id))) {
        setTrialModalOpen(false);
        return;
      }
    } catch (_) { /* sessionStorage may be unavailable */ }
    setTrialModalOpen(true);
  }, [unpaidTrial, user?.id, onSparkConnect]);

  const dismissTrialModal = useCallback(() => {
    setTrialModalOpen(false);
    try {
      sessionStorage.setItem(trialDismissKey(user?.id), "1");
    } catch (_) { /* ignore */ }
  }, [user?.id]);

  const goTrialPricing = () => {
    setMobileMenuOpen(false);
    navigate(TRIAL_PRICING_PATH);
  };

  const trialDays = connectStatus?.days_remaining;
  const trialTooltip = trialDays != null
    ? `${trialDays} day${trialDays === 1 ? "" : "s"} left — 10% off if you subscribe now`
    : "10% off if you subscribe before your trial ends";

  useEffect(() => {
    if (!isAuthenticated) {
      setCreditBalance(null);
      return;
    }
    if (typeof user?.credit_balance === "number") {
      setCreditBalance(user.credit_balance);
    }
    const fetchBalance = () => {
      api.get(`/credits/balance`)
        .then(r => setCreditBalance(r.data?.credit_balance ?? 0))
        .catch(() => {});
    };
    fetchBalance();
    const timer = setInterval(fetchBalance, 60000);
    return () => clearInterval(timer);
  }, [isAuthenticated, user?.credit_balance]);

  const scrollToSection = (sectionId) => {
    if (location.pathname === '/' || location.pathname === '/home') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(`/home#${sectionId}`);
    }
    setMobileMenuOpen(false);
  };

  const handleHomeClick = (e) => {
    if (location.pathname === '/' || location.pathname === '/home') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const mobileLinkClass =
    "min-h-11 flex items-center px-4 text-base text-neutral-800 dark:text-white/90 hover:bg-neutral-100 dark:hover:bg-white/10";

  return (
    <>
    <nav className="creator-shell fixed top-0 left-0 right-0 z-50 h-16 border-b border-neutral-200 dark:border-white/10 bg-white dark:bg-neutral-950">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={isAuthenticated ? '/dashboard' : '/home'} onClick={isAuthenticated ? undefined : handleHomeClick} className="flex items-center group">
            <img src={logoImage} alt="SparkSeller" className="h-12 group-hover:opacity-90 transition-opacity" />
          </Link>

          {/* Desktop Navigation */}
          {!isAuthenticated ? (
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/home" onClick={handleHomeClick} className={`text-neutral-500 hover:text-neutral-900 dark:text-white/60 dark:hover:text-white transition-colors ${isActive('/home') || isActive('/') ? 'text-neutral-900 dark:text-white font-semibold' : ''}`}>
                Home
              </Link>
              <button 
                onClick={() => scrollToSection('how-we-help')} 
                className="text-neutral-500 hover:text-neutral-900 dark:text-white/60 dark:hover:text-white transition-colors"
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className="text-neutral-500 hover:text-neutral-900 dark:text-white/60 dark:hover:text-white transition-colors"
              >
                Contact
              </button>
              <Link to="/affiliates" className={`flex items-center gap-1.5 text-neutral-500 hover:text-neutral-900 dark:text-white/60 dark:hover:text-white transition-colors ${isActive('/affiliates') ? 'text-neutral-900 dark:text-white font-semibold' : ''}`}>
                <Gift className="w-4 h-4" />
                Affiliates
              </Link>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 dark:text-white/50 dark:hover:text-white dark:hover:bg-white/10 transition-colors"
                title={THEMES[theme]?.dark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {THEMES[theme]?.dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="rounded-full border-neutral-200 dark:border-white/10 text-neutral-700 dark:text-white hover:bg-neutral-100 dark:hover:bg-white/5"
              >
                Login
              </Button>
              <Button
                onClick={() => navigate('/signup')}
                className="rounded-full bg-orange-500 hover:bg-orange-400 text-black font-medium"
              >
                Get Started
              </Button>
            </div>
          ) : (
            <div className="hidden md:flex items-center space-x-6">
              <a
                href={CREATOR_PROD_URL}
                className="text-neutral-500 hover:text-neutral-900 dark:text-white/60 dark:hover:text-white transition-colors"
                title="Open SparkSeller Creator"
              >
                Creator
              </a>

              {user?.can_access_walmart && (
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center text-neutral-500 hover:text-neutral-900 dark:text-white/60 dark:hover:text-white transition-colors focus:outline-none">
                    Connect <ChevronDown className="ml-1 h-4 w-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-white dark:bg-neutral-900 border-neutral-200 dark:border-white/10">
                    {visibleSparkConnectNav.map(item => {
                      const sectionKey = item.key === "incentives" ? "pricing" : item.key;
                      const allowed = canSection(sectionKey) && !trialExpired;
                      const expiredLock = trialExpired;
                      return (
                      <DropdownMenuItem
                        key={item.key}
                        disabled={!allowed && !expiredLock}
                        title={
                          expiredLock
                            ? "Subscribe to continue using SparkSeller Connect."
                            : (!allowed ? "You don't have access to this section." : undefined)
                        }
                        className={allowed
                          ? "text-neutral-700 dark:text-white/80 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 cursor-pointer"
                          : "text-neutral-400 dark:text-white/30 opacity-50 cursor-not-allowed"}
                        onClick={() => {
                          if (expiredLock) navigate(TRIAL_PRICING_PATH);
                          else if (allowed) navigate(item.path);
                        }}
                      >
                        {item.label}
                      </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              {/* Background Task Indicator */}
              {isPolling && (
                pollingJobs.length === 1 ? (
                  <button
                    onClick={() => pollingJobs[0].onReopen?.()}
                    title={`${pollingJobs[0].label}${pollingJobs[0].phase ? ` — ${pollingJobs[0].phase}` : ''} — click to view`}
                    className="relative p-2 rounded-lg text-teal-500 hover:text-teal-400 hover:bg-teal-500/10 transition-colors"
                  >
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        title={`${pollingJobs.length} tasks running — click to view`}
                        className="relative p-2 rounded-lg text-teal-500 hover:text-teal-400 hover:bg-teal-500/10 transition-colors"
                      >
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="absolute -top-0.5 -right-0.5 bg-teal-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                          {pollingJobs.length}
                        </span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white dark:bg-neutral-900 border-neutral-200 dark:border-white/10">
                      {pollingJobs.map(job => (
                        <DropdownMenuItem
                          key={job.id}
                          className="text-neutral-700 dark:text-white/80 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 cursor-pointer"
                          onClick={() => job.onReopen?.()}
                        >
                          <Loader2 className="w-3 h-3 mr-2 text-teal-500 animate-spin" />
                          <span className="flex flex-col">
                            <span className="text-sm">{job.label}</span>
                            {job.phase && <span className="text-xs text-gray-500 dark:text-gray-400">{job.phase}</span>}
                          </span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              )}

              {unpaidTrial && (
                <button
                  type="button"
                  onClick={goTrialPricing}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-400"
                  title={trialTooltip}
                >
                  <Clock className="w-3.5 h-3.5" />
                  <span className="text-xs font-semibold">Trial</span>
                </button>
              )}

              {creditBalance !== null && (
                <button
                  type="button"
                  onClick={() => openCreator("/spark-creator/credits")}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500 text-black text-sm font-medium hover:bg-orange-400"
                  title="Credits — click to buy more on Creator"
                >
                  <Zap className="w-3.5 h-3.5" />
                  <span className="text-xs font-semibold">
                    {creditBalance.toLocaleString()}
                  </span>
                </button>
              )}

              {hasTutorial && (
                <button
                  type="button"
                  onClick={openTutorial}
                  className="p-2 rounded-full text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 dark:text-white/50 dark:hover:text-white dark:hover:bg-white/10 transition-colors"
                  title="Page guide"
                >
                  <CircleHelp className="w-5 h-5" />
                </button>
              )}

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 dark:text-white/50 dark:hover:text-white dark:hover:bg-white/10 transition-colors"
                title={THEMES[theme]?.dark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {THEMES[theme]?.dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              
              <div className="flex items-center pl-4 border-l border-neutral-200 dark:border-white/10">
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900 dark:text-white/70 dark:hover:text-white focus:outline-none cursor-pointer">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-teal-500 text-white flex items-center justify-center text-xs font-semibold">
                      {userInitial}
                    </div>
                    <ChevronDown className="w-3.5 h-3.5 text-neutral-400 dark:text-white/40" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white dark:bg-neutral-900 border-neutral-200 dark:border-white/10 min-w-[160px]">
                    <DropdownMenuItem 
                      className="text-neutral-700 dark:text-white/80 hover:bg-neutral-100 dark:hover:bg-white/10 cursor-pointer"
                      onClick={() => navigate('/settings')}
                    >
                      <Settings className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" /> Settings
                    </DropdownMenuItem>
                    {isAffiliate && (
                      <DropdownMenuItem
                        className="text-orange-600 dark:text-orange-400 hover:bg-neutral-100 dark:hover:bg-white/10 cursor-pointer"
                        onClick={() => navigate('/affiliate-dashboard')}
                      >
                        <Gift className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" /> Affiliate Program
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      className="text-blue-600 dark:text-blue-400 hover:bg-neutral-100 dark:hover:bg-white/10 cursor-pointer"
                      onClick={() => navigate('/spark-connect')}
                    >
                      <CreditCard className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" /> Subscription
                    </DropdownMenuItem>
                    {user?.is_admin && (
                      <DropdownMenuItem 
                        className="text-amber-600 dark:text-amber-400 hover:bg-neutral-100 dark:hover:bg-white/10 cursor-pointer"
                        onClick={() => navigate('/admin')}
                      >
                        <Shield className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" /> Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      className="text-rose-600 dark:text-rose-400 hover:bg-neutral-100 dark:hover:bg-white/10 cursor-pointer"
                      onClick={onLogout}
                    >
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="md:hidden min-h-11 min-w-11 flex items-center justify-center text-gray-900 dark:text-white"
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
    </nav>

    {mobileMenuOpen && (
      <div className="mobile-nav-overlay md:hidden fixed inset-x-0 top-16 z-50 flex h-[calc(100dvh-4rem)] flex-col">
        <div className="flex-1 overflow-y-auto overscroll-contain pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            {!isAuthenticated ? (
              <div className="flex flex-col py-2">
                <Link to="/home" className={mobileLinkClass} onClick={handleHomeClick}>
                  Home
                </Link>
                <button
                  type="button"
                  onClick={() => scrollToSection('how-we-help')}
                  className={`${mobileLinkClass} text-left w-full`}
                >
                  Features
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection('contact')}
                  className={`${mobileLinkClass} text-left w-full`}
                >
                  Contact
                </button>
                <Link to="/affiliates" className={`${mobileLinkClass} gap-2`} onClick={() => setMobileMenuOpen(false)}>
                  <Gift className="w-4 h-4" />
                  Affiliates
                </Link>
                <button
                  type="button"
                  onClick={() => { toggleTheme(); setMobileMenuOpen(false); }}
                  className={`${mobileLinkClass} w-full text-left`}
                >
                  {THEMES[theme]?.dark ? <Sun className="w-5 h-5 mr-2" /> : <Moon className="w-5 h-5 mr-2" />}
                  {THEMES[theme]?.dark ? 'Light Mode' : 'Dark Mode'}
                </button>
                <div className="px-4 pt-4 space-y-2">
                  <Button
                    variant="outline"
                    onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                    className="w-full min-h-11 rounded-full border-neutral-200 dark:border-white/10"
                  >
                    Login
                  </Button>
                  <Button
                    onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }}
                    className="w-full min-h-11 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-medium"
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col py-2">
                <a
                  href={CREATOR_PROD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={mobileLinkClass}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Creator
                </a>

                {user?.can_access_walmart && (
                  <div>
                    <button
                      type="button"
                      onClick={() => setConnectOpen(o => !o)}
                      className={`${mobileLinkClass} w-full justify-between font-medium`}
                    >
                      <span>Connect</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${connectOpen ? "rotate-180" : ""}`} />
                    </button>
                    {connectOpen && visibleSparkConnectNav.map(item => {
                      const sectionKey = item.key === "incentives" ? "pricing" : item.key;
                      const allowed = canSection(sectionKey) && !trialExpired;
                      if (trialExpired) {
                        return (
                      <button
                        key={item.key}
                        type="button"
                        title="Subscribe to continue using SparkSeller Connect."
                        className={`${mobileLinkClass} pl-8 w-full text-left text-neutral-400 dark:text-white/30 opacity-50`}
                        onClick={() => { navigate(TRIAL_PRICING_PATH); setMobileMenuOpen(false); }}
                      >
                        {item.label}
                      </button>
                        );
                      }
                      return allowed ? (
                      <Link
                        key={item.key}
                        to={item.path}
                        className={`${mobileLinkClass} pl-8 ${isActive(item.path) ? "font-semibold bg-neutral-100 dark:bg-white/10" : ""}`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                      ) : (
                      <span
                        key={item.key}
                        title="You don't have access to this section."
                        className={`${mobileLinkClass} pl-8 text-neutral-400 dark:text-white/30`}
                      >
                        {item.label}
                      </span>
                      );
                    })}
                  </div>
                )}
                {user?.is_admin && (
                  <Link to="/admin" className={`${mobileLinkClass} gap-2 text-amber-700 dark:text-amber-400`} onClick={() => setMobileMenuOpen(false)}>
                    <Shield className="w-4 h-4" />
                    Admin Dashboard
                  </Link>
                )}
                {hasTutorial && (
                  <button
                    type="button"
                    onClick={() => { openTutorial(); setMobileMenuOpen(false); }}
                    className={`${mobileLinkClass} w-full text-left`}
                  >
                    <CircleHelp className="w-5 h-5 mr-2" />
                    Page guide
                  </button>
                )}
                <Link to="/settings" className={`${mobileLinkClass} gap-2`} onClick={() => setMobileMenuOpen(false)}>
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
                <Link to="/spark-connect" className={`${mobileLinkClass} gap-2 text-blue-600 dark:text-blue-400`} onClick={() => setMobileMenuOpen(false)}>
                  <CreditCard className="w-4 h-4" />
                  Subscription
                </Link>
                {isAffiliate && (
                  <Link to="/affiliate-dashboard" className={`${mobileLinkClass} gap-2 text-orange-600 dark:text-orange-400`} onClick={() => setMobileMenuOpen(false)}>
                    <Gift className="w-4 h-4" />
                    Affiliate Program
                  </Link>
                )}
                {unpaidTrial && (
                  <button
                    type="button"
                    onClick={goTrialPricing}
                    className="mx-4 mt-2 mb-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-blue-500 text-white text-sm font-semibold hover:bg-blue-400"
                    title={trialTooltip}
                  >
                    <Clock className="w-3.5 h-3.5" />
                    Trial
                  </button>
                )}
                {creditBalance !== null && (
                  <button
                    type="button"
                    onClick={() => { openCreator("/spark-creator/credits"); setMobileMenuOpen(false); }}
                    className={`${mobileLinkClass} w-full text-left gap-2`}
                  >
                    <Zap className="w-4 h-4 text-orange-500" />
                    {creditBalance.toLocaleString()} credits
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { toggleTheme(); setMobileMenuOpen(false); }}
                  className={`${mobileLinkClass} w-full text-left`}
                >
                  {THEMES[theme]?.dark ? <Sun className="w-5 h-5 mr-2" /> : <Moon className="w-5 h-5 mr-2" />}
                  {THEMES[theme]?.dark ? 'Light Mode' : 'Dark Mode'}
                </button>
                {isPolling && (
                  <div className="border-t border-neutral-200 dark:border-white/10 mt-1 pt-1">
                    {pollingJobs.map(job => (
                      <button
                        key={job.id}
                        type="button"
                        onClick={() => { job.onReopen?.(); setMobileMenuOpen(false); }}
                        className={`${mobileLinkClass} w-full text-left text-teal-600 dark:text-teal-400`}
                      >
                        <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" />
                        <span className="truncate">{job.label}{job.phase ? ` — ${job.phase}` : ""}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="px-4 pt-4 mt-2 border-t border-neutral-200 dark:border-white/10">
                  <div className="flex items-center gap-2 mb-3 min-h-11">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-teal-500 text-white flex items-center justify-center text-sm font-semibold">
                      {userInitial}
                    </div>
                    <span className="text-sm text-neutral-600 dark:text-white/60 truncate">{user?.name || user?.email}</span>
                  </div>
                  <Button variant="outline" onClick={() => { onLogout(); setMobileMenuOpen(false); }} className="w-full min-h-11 rounded-full">
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </Button>
                </div>
              </div>
            )}
        </div>
      </div>
    )}
    <TrialReminderDialog
      open={trialModalOpen}
      daysRemaining={trialDays}
      onDismiss={dismissTrialModal}
    />
    </>
  );
};

export default Navbar;
